import { defaultGhExec, type GhExec } from "./gh-helpers.js";

/** One GitHub organization the signed-in user belongs to. */
export interface OrgInfo {
  login: string;
  /** True when the org is linked to a GitHub Enterprise account. */
  enterprise: boolean;
  /** True when the signed-in user can administer the org. */
  canAdminister: boolean;
}

export type OrgVerdict =
  | "enterprise-single"
  | "enterprise-multiple"
  | "no-enterprise"
  | "no-orgs"
  | "undetermined";

/** Either usable org data, or an explicit failure. Never a silent empty list. */
export type OrgGraphResult =
  | { ok: true; orgs: OrgInfo[] }
  | { ok: false; reason: string };

export interface OrgClassification {
  verdict: OrgVerdict;
  /** Set only for "enterprise-single". */
  selected?: string;
  /** The orgs a caller should offer. Empty for "no-orgs" and "undetermined". */
  candidates: OrgInfo[];
  /** Every org observed. Empty when detection failed. */
  orgs: OrgInfo[];
}

// One call returns the org list, the caller's admin role, and enterprise linkage.
// enterpriseOwners is readable with the plain `read:org` scope that `gh auth login`
// grants by default; the `viewer.enterprises` field is NOT (it needs read:enterprise,
// which would force every founder through a re-auth).
const ORG_QUERY =
  "{ viewer { organizations(first:50) { nodes { login viewerCanAdminister enterpriseOwners(first:1) { totalCount } } } } }";

interface GraphNode {
  login?: unknown;
  viewerCanAdminister?: unknown;
  enterpriseOwners?: { totalCount?: unknown } | null;
}

interface GraphResponse {
  data?: { viewer?: { organizations?: { nodes?: unknown } } };
  errors?: unknown;
}

/**
 * Classify an org graph result into the verdict a caller acts on.
 *
 * Pure: no I/O, so the whole verdict matrix is testable from fixtures.
 *
 * An enterprise org is preferred whenever one is visible. When none is, the
 * caller keeps its existing behavior — this is an enhancement layered over the
 * unchanged flow, never a gate.
 */
export function classifyOrgs(result: OrgGraphResult): OrgClassification {
  if (!result.ok) return { verdict: "undetermined", candidates: [], orgs: [] };

  const orgs = result.orgs;
  if (orgs.length === 0) return { verdict: "no-orgs", candidates: [], orgs };

  const enterprise = orgs.filter((o) => o.enterprise);
  if (enterprise.length === 1) {
    return { verdict: "enterprise-single", selected: enterprise[0].login, candidates: enterprise, orgs };
  }
  if (enterprise.length > 1) {
    return { verdict: "enterprise-multiple", candidates: enterprise, orgs };
  }
  return { verdict: "no-enterprise", candidates: orgs, orgs };
}

/**
 * Read the signed-in user's orgs and their enterprise linkage via `gh`.
 *
 * Read-only. Every failure mode - gh missing, not signed in, non-JSON output,
 * an unexpected shape, or a partial result carrying field errors - resolves to
 * `{ ok: false }` so the caller degrades to asking rather than acting on a
 * value it cannot trust.
 */
export async function fetchOrgGraph(exec: GhExec = defaultGhExec): Promise<OrgGraphResult> {
  const r = await exec("gh", ["api", "graphql", "-f", `query=${ORG_QUERY}`]);
  if (r.exitCode !== 0) {
    return { ok: false, reason: r.stderr.trim() || `gh exited ${r.exitCode.toString()}` };
  }

  let parsed: GraphResponse;
  try {
    parsed = JSON.parse(r.stdout) as GraphResponse;
  } catch {
    return { ok: false, reason: "gh returned output that is not JSON" };
  }

  // GraphQL can return `data` alongside per-field `errors` (e.g. a FORBIDDEN
  // enterpriseOwners). Treating that missing field as 0 would assert "no
  // enterprise org" from data we were not allowed to read.
  if (Array.isArray(parsed.errors) && parsed.errors.length > 0) {
    return { ok: false, reason: "the org query returned a partial result" };
  }

  const nodes = parsed.data?.viewer?.organizations?.nodes;
  if (!Array.isArray(nodes)) return { ok: false, reason: "unexpected response shape" };

  const orgs: OrgInfo[] = [];
  // The element type admits null deliberately: this array came from JSON, and
  // GraphQL returns a null node for an org it could not resolve. Casting it to
  // a non-nullable element type would make the guards below look redundant to
  // the type checker while a null node still crashed at runtime.
  for (const raw of nodes as (GraphNode | null)[]) {
    if (raw === null) return { ok: false, reason: "unexpected response shape" };
    const login = raw.login;
    const total = raw.enterpriseOwners?.totalCount;
    if (typeof login !== "string" || typeof total !== "number") {
      return { ok: false, reason: "unexpected response shape" };
    }
    orgs.push({ login, enterprise: total > 0, canAdminister: raw.viewerCanAdminister === true });
  }
  return { ok: true, orgs };
}
